import { Button, Form, Input, Space } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
    DatePicker as ZhDatePicker,
    DetailHeader as ZhDetailHeader,
    Grid as ZhGrid,
    GridItem as ZhGridItem,
    InputNumber as ZhInputNumber,
    Select as ZhSelect
} from "zhihao-ui-react";

import request from "@/api";
import { ADVERTISING_WATCH_TIME, IS_INCLUDE_TAX, WAITING_PAY_TIME } from "@zhihaoscm/meta";
import { MediaUploadCard } from "./ConsignmentFormPage";
import type { BargainFormValues, BargainRow } from "./types";
import "./ConsignmentFormPage.scss";

type BargainMediaFormValues = BargainFormValues & {
    imgMainId?: number;
    videoId?: number;
};

interface BargainUploadedFile {
    id: number;
    path?: string;
}

export interface BargainFormPageProps {
    loadBargain?: (id: number | string) => Promise<BargainRow>;
    saveBargain?: (id: number | string | undefined, values: BargainMediaFormValues) => Promise<void>;
}

/** 复用 Vue 捡漏商品详情接口，为编辑表单回填数据。 */
export async function loadBargain(id: number | string): Promise<BargainRow> {
    const response = await request.service.get<BargainRow>(`/admin/product-bargain/vo/${id}`);
    return response.data;
}

/** 复用 Vue 捡漏商品新增草稿和更新草稿接口。 */
export async function saveBargain(id: number | string | undefined, values: BargainMediaFormValues): Promise<void> {
    if (id == null) {
        await request.service.post("/admin/product-bargain/create-save", values);
    } else {
        await request.service.post(`/admin/product-bargain/update-save/${id}`, values);
    }
}

/** 复用 Vue upload-list 的文件接口，返回后端文件 ID 和访问路径。 */
export async function uploadBargainMedia(file: Blob): Promise<BargainUploadedFile> {
    const formData = new FormData();
    formData.append("file", file);
    const { fileService } = await import("@/api/services/fileService");
    const uploadedFile = await fileService.uploadFile(formData);
    return { id: Number(uploadedFile.id), path: uploadedFile.path };
}

const IMAGE_MAX_SIZE = 2 * 1024 * 1024;
const VIDEO_MAX_SIZE = 200 * 1024 * 1024;

/** 按 Vue 上传组件的扩展名和 MIME 类型校验图片或视频。 */
function validateBargainMedia(file: File, mediaType: "image" | "video"): string | null {
    const extension = file.name.split(".").pop()?.toLowerCase();
    if (mediaType === "image") {
        const validType = file.type === "image/jpeg" || file.type === "image/png";
        const validExtension = extension === "jpg" || extension === "jpeg" || extension === "png";
        if (!validType || !validExtension) return "主图仅支持 JPEG、PNG 格式";
        if (file.size > IMAGE_MAX_SIZE) return "主图大小不能超过 2MB";
    } else {
        const validType = file.type === "video/mp4" || file.type === "video/quicktime";
        const validExtension = extension === "mp4" || extension === "mov";
        if (!validType || !validExtension) return "视频仅支持 MOV、MP4 格式";
        if (file.size > VIDEO_MAX_SIZE) return "视频大小不能超过 200MB";
    }
    return null;
}

/** 捡漏商品新增/编辑页，覆盖 Vue 表单的核心价格、数量、时间和介绍字段。 */
export function BargainFormPage({
    loadBargain: load = loadBargain,
    saveBargain: save = saveBargain
}: BargainFormPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const query = new URLSearchParams(location.search);
    const id = query.get("id") || undefined;
    const [form] = Form.useForm<BargainMediaFormValues>();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [mediaIds, setMediaIds] = useState<{ imgMainId?: number; videoId?: number }>({});
    const [mediaPaths, setMediaPaths] = useState<{ imgMainPath?: string; videoPath?: string }>({});
    const bargainUnitPrice = Form.useWatch("bargainUnitPrice", form);
    const bargainTon = Form.useWatch("bargainTon", form);
    // 沿用 Vue 表单的实时计算口径：捡漏单价乘以捡漏吨数，未形成有效金额时显示占位符。
    const totalCost = useMemo(() => {
        const unitPrice = Number(bargainUnitPrice);
        const ton = Number(bargainTon);
        if (!Number.isFinite(unitPrice) || !Number.isFinite(ton) || !unitPrice || !ton) return null;
        return unitPrice * ton;
    }, [bargainTon, bargainUnitPrice]);

    useEffect(() => {
        if (!id) {
            form.setFieldsValue({ title: "", weight: 0 });
            setMediaIds({});
            setMediaPaths({});
            return;
        }

        void load(id)
            .then(result => {
                setErrorMessage(null);
                const product = result.productBargain as BargainMediaFormValues;
                // 编辑回显优先使用表单字段，兼容 Vue 详情接口将文件 ID 放在文件对象中的返回结构。
                const imgMainId = product.imgMainId ?? result.mainFile?.id;
                const videoId = product.videoId ?? result.videoFile?.id;
                setMediaIds({ imgMainId, videoId });
                setMediaPaths({ imgMainPath: result.mainFile?.path, videoPath: result.videoFile?.path });
                form.setFieldsValue(product);
            })
            .catch(() => setErrorMessage("捡漏商品详情加载失败"));
    }, [form, id, load]);

    /** 上传单个媒体文件，并将 Vue 契约中的文件 ID回填到待保存状态。 */
    const handleMediaChange = async (file: File | undefined, mediaType: "image" | "video") => {
        if (!file) return;
        const validationError = validateBargainMedia(file, mediaType);
        if (validationError) {
            setErrorMessage(validationError);
            return;
        }

        setErrorMessage(null);
        try {
            const uploadedFile = await uploadBargainMedia(file);
            if (!uploadedFile.id) throw new Error("missing file id");
            if (mediaType === "image") {
                setMediaIds(current => ({ ...current, imgMainId: uploadedFile.id }));
                setMediaPaths(current => ({ ...current, imgMainPath: uploadedFile.path }));
            } else {
                setMediaIds(current => ({ ...current, videoId: uploadedFile.id }));
                setMediaPaths(current => ({ ...current, videoPath: uploadedFile.path }));
            }
        } catch {
            setErrorMessage(`捡漏商品${mediaType === "image" ? "主图" : "视频"}上传失败`);
        } finally {
        }
    };

    const submit = async () => {
        try {
            const values = await form.validateFields();
            // 仅在用户已有文件或完成上传时附加 ID，确保未涉及媒体时保存 payload 保持原字段集合。
            const payload: BargainMediaFormValues = { ...values };
            if (mediaIds.imgMainId != null) payload.imgMainId = mediaIds.imgMainId;
            if (mediaIds.videoId != null) payload.videoId = mediaIds.videoId;
            await save(id, payload);
            void navigate("/productManagement/bargain");
        } catch {
            setErrorMessage("捡漏商品保存失败");
        }
    };

    return (
        <section className="consignment-form-page bargain-form-page" aria-label="bargain-form-page">
            <section className="section-card consignment-form__header">
                <ZhDetailHeader title={id ? "编辑捡漏商品" : "新增捡漏商品"} />
            </section>
            <section className="section-card consignment-form__section">
                <h2 className="section-title">商品信息</h2>
                {errorMessage ? <p role="alert">{errorMessage}</p> : null}
                <Form
                    form={form}
                    className="consignment-form__form"
                    colon={false}
                    labelAlign="right"
                    labelCol={{ flex: "128px" }}
                    wrapperCol={{ flex: 1 }}
                >
                    <ZhGrid cols={4} gap={[16, 0]}>
                        <ZhGridItem>
                            <Form.Item
                                label="商品标题"
                                name="title"
                                rules={[{ required: true, message: "请输入商品标题" }]}
                            >
                                <Input />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="权重值" name="weight">
                                <ZhInputNumber className="w-full" min={0} />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="捡漏单价" name="bargainUnitPrice">
                                <ZhInputNumber className="w-full" min={0} />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="是否含税" name="isIncludeTax">
                                <ZhSelect
                                    aria-label="是否含税"
                                    options={IS_INCLUDE_TAX.options.map(option => ({
                                        label: option.label,
                                        value: Number(option.value)
                                    }))}
                                    placeholder="请选择是否含税"
                                />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="市场价" name="unitPrice">
                                <ZhInputNumber className="w-full" min={0} precision={2} />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="捡漏吨数" name="bargainTon">
                                <ZhInputNumber className="w-full" min={0} />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="总价">
                                <span data-testid="bargain-total-cost">
                                    {totalCost == null ? "-" : `${totalCost} 元`}
                                </span>
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="捡漏开始时间" name="bargainStartTime">
                                <ZhDatePicker
                                    className="w-full"
                                    format="YYYY-MM-DD HH:mm"
                                    placeholder="请选择捡漏开始时间"
                                    type="datetime"
                                    valueFormat="YYYY-MM-DD HH:mm:ss"
                                />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="活动天数" name="bargainCycle">
                                <ZhSelect
                                    aria-label="活动天数"
                                    options={Array.from({ length: 30 }, (_, index) => ({
                                        label: `+${index + 1}`,
                                        value: index + 1
                                    }))}
                                    placeholder="请选择活动天数"
                                />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="捡漏截止时间" name="bargainEndTime">
                                <ZhDatePicker
                                    className="w-full"
                                    format="YYYY-MM-DD HH:mm"
                                    placeholder="请选择捡漏截止时间"
                                    type="datetime"
                                    valueFormat="YYYY-MM-DD HH:mm:ss"
                                />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="广告观看时间" name="advertisingWatchTime">
                                <ZhSelect
                                    aria-label="广告观看时间"
                                    options={ADVERTISING_WATCH_TIME.options.map(option => ({
                                        label: option.label,
                                        value: Number(option.value)
                                    }))}
                                    placeholder="请选择广告观看时间"
                                />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="等待付款时间" name="waitingPayTime">
                                <ZhSelect
                                    aria-label="等待付款时间"
                                    options={WAITING_PAY_TIME.options.map(option => ({
                                        label: option.label,
                                        value: Number(option.value)
                                    }))}
                                    placeholder="请选择等待付款时间"
                                />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={4}>
                            <Form.Item label="图片">
                                <MediaUploadCard
                                    type="image"
                                    path={mediaPaths.imgMainPath || null}
                                    inputAriaLabel="上传捡漏商品主图"
                                    onChange={event => void handleMediaChange(event.currentTarget.files?.[0], "image")}
                                />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={4}>
                            <Form.Item label="视频">
                                <MediaUploadCard
                                    type="video"
                                    path={mediaPaths.videoPath || null}
                                    inputAriaLabel="上传捡漏商品视频"
                                    onChange={event => void handleMediaChange(event.currentTarget.files?.[0], "video")}
                                />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={4}>
                            <Form.Item label="助力广告">
                                <div className="bargain-form__advert-upload">
                                    <MediaUploadCard type="image" path={null} onChange={() => undefined} inputIdPrefix="bargain-ad-image" />
                                    <MediaUploadCard type="video" path={null} onChange={() => undefined} inputIdPrefix="bargain-ad-video" />
                                </div>
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="规格" name="productTypeSpec">
                                <Input placeholder="请输入规格" />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={4}>
                            <Form.Item label="商品介绍" name="introduce">
                                <Input.TextArea rows={5} />
                            </Form.Item>
                        </ZhGridItem>
                    </ZhGrid>
                    <Space>
                        <Button type="primary" onClick={() => void submit()}>
                            保存捡漏商品
                        </Button>
                        <Button onClick={() => void navigate("/productManagement/bargain")}>返回</Button>
                    </Space>
                </Form>
            </section>
        </section>
    );
}

export default BargainFormPage;
